import React, { useEffect, useState } from "react";
import TemplateMessage from "./TemplateMessage";
import MediaMessage from "./MediaMessage";
import PayloadMessage from "./PayloadMessage";
import StatusMessage from "./StatusMessage";
import { useAuth } from "../services/auth.service";
import SurveyMessage from "./SurveyMessage";
import { Box, Typography, Avatar, Chip, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components for message bubbles
const MessageBubble = styled(Box)(({ theme, isSender, isStatus }) => ({
  position: 'relative',
  padding: isStatus ? '8px 16px' : '12px 16px',
  borderRadius: isSender 
    ? '18px 18px 4px 18px' 
    : '18px 18px 18px 4px',
  maxWidth: '70%',
  minWidth: '120px',
  backgroundColor: isSender 
    ? '#008069' 
    : isStatus 
      ? '#f0f2f5' 
      : '#ffffff',
  color: isSender ? '#ffffff' : '#111b21',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
  border: isStatus ? '1px solid #e9edef' : 'none',
  marginBottom: '8px',
  wordWrap: 'break-word',
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  }
}));

const TimeStamp = styled(Typography)(({ theme, isSender }) => ({
  fontSize: '11px',
  color: isSender ? 'rgba(255, 255, 255, 0.7)' : '#667781',
  marginTop: '4px',
  textAlign: isSender ? 'right' : 'left',
  fontWeight: 400,
}));

const MessageContainer = styled(Box)(({ theme, isSender, isStatus }) => ({
  display: 'flex',
  justifyContent: isStatus ? 'center' : isSender ? 'flex-end' : 'flex-start',
  alignItems: 'flex-end',
  marginBottom: '16px',
  gap: '8px',
  padding: '0 16px',
}));

const AvatarStyled = styled(Avatar)(({ theme, isSender }) => ({
  width: '32px',
  height: '32px',
  backgroundColor: isSender ? '#008069' : '#f0f2f5',
  color: isSender ? '#ffffff' : '#667781',
  fontSize: '14px',
  fontWeight: 600,
}));

const ReactionChip = styled(Chip)(({ theme, isSender }) => ({
  fontSize: '12px',
  backgroundColor: isSender ? 'rgba(255, 255, 255, 0.1)' : '#f0f2f5',
  color: isSender ? '#ffffff' : '#667781',
  border: isSender ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #e9edef',
  marginTop: '4px',
  '& .MuiChip-label': {
    fontSize: '11px',
    padding: '2px 6px',
  },
}));

const ButtonResponseChip = styled(Box)(({ theme, isSender }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  backgroundColor: isSender ? 'rgba(255, 255, 255, 0.15)' : '#e8f5e8',
  color: isSender ? '#ffffff' : '#008069',
  border: isSender ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid #d4edda',
  borderRadius: '12px',
  padding: '4px 8px',
  marginTop: '4px',
  fontSize: '11px',
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: isSender ? 'rgba(255, 255, 255, 0.2)' : '#d1e7dd',
  },
}));

function Message(props) {
  const [message, setMessage] = useState(null);
  const [statuses, setStatuses] = useState(null);
  const [error, setError] = useState(false);
  const { user } = useAuth();
  const surveyId = "65cfa291d2cf9544d0ee5667";
  const [survey, setSurvey] = React.useState();
  const [isReaction, setIsReaction] = useState(false);
  const [parentMessage, setParentMessage] = useState(null);
  const [buttonResponses, setButtonResponses] = useState([]);

  const getNormalizedTextBody = (msg) => {
    const rawBody = msg?.text?.body;
    if (!rawBody) return rawBody;
    if (typeof rawBody === "object" && rawBody.body && typeof rawBody.body === "object") {
      return rawBody.body;
    }
    return rawBody;
  };

  useEffect(() => {
    const newMessage =
      props.message?.entry[0]?.changes[0]?.value?.messages[0] || null;
    const lastStatus = props.message?.entry[0]?.changes[0]?.value?.statuses?.slice(-1)[0] || null;
    setStatuses(lastStatus || null);
    setMessage(newMessage);
    
    // Debug logging for media messages
    if (newMessage && (newMessage.type === "file" || newMessage.type === "image" || newMessage.type === "video" || newMessage.type === "audio" || newMessage.type === "document")) {
      console.log('Media message detected:', newMessage);
    }
    
    // Check if this is a reaction message
    if (newMessage?.type === "reaction") {
      setIsReaction(true);
      // Find the parent message that this reaction belongs to
      const reactedMessageId = newMessage.reaction.message_id;
      // You'll need to pass the messages array as a prop to find the parent
      if (props.messages && props.messages.length > 0) {
        const parent = props.messages.find(msg => {
          try {
            return msg.entry[0].changes[0].value.messages[0].id === reactedMessageId;
          } catch (error) {
            return false;
          }
        });
        setParentMessage(parent);
      }
    } else {
      setIsReaction(false);
      setParentMessage(null);
    }

    // Find button responses for this message
    if (newMessage && props.messages && props.messages.length > 0) {
      const responses = props.messages.filter(msg => {
        try {
          const responseMsg = msg.entry[0].changes[0].value.messages[0];
          return responseMsg?.type === "button" && 
                 responseMsg?.context?.id === newMessage.id;
        } catch (error) {
          return false;
        }
      });
      setButtonResponses(responses);
    } else {
      setButtonResponses([]);
    }
  }, [props.message, props.messages]);

  useEffect(() => {
    const lastStatus = props.message?.entry[0]?.changes[0]?.value?.statuses?.slice(-1)[0] || null;
    setStatuses(lastStatus || null);
  }, [JSON.stringify(props.message?.entry[0]?.changes[0]?.value?.statuses)]);

  useEffect(() => {
    if (message?.type === "text") {
      if (message?.text?.body?.name) {
        console.log("Template message");
      } else {
        if (message?.text.body.text) {
          console.log("Text message with subtext")
        }
        console.log("Text message");
      }
    } else {
      console.log("Media message");
    }
  }, [message]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const renderButtonResponses = (isSender) => {
    if (!buttonResponses || buttonResponses.length === 0) return null;

    return buttonResponses.map((responseMsg, idx) => {
      try {
        const buttonMsg = responseMsg.entry[0].changes[0].value.messages[0];
        const buttonText = buttonMsg?.button?.text || 'Button Response';
        const buttonPayload = buttonMsg?.button?.payload || '';
        const timestamp = formatTime(buttonMsg?.timestamp);
        
        const tooltipContent = (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Button Response Details:
            </Typography>
            <Typography variant="caption" display="block">
              Text: {buttonText}
            </Typography>
            <Typography variant="caption" display="block">
              Payload: {buttonPayload}
            </Typography>
            <Typography variant="caption" display="block">
              Time: {timestamp}
            </Typography>
            <Typography variant="caption" display="block">
              ID: {buttonMsg?.id}
            </Typography>
          </Box>
        );

        return (
          <Tooltip key={idx} title={tooltipContent} arrow placement="top">
            <ButtonResponseChip isSender={isSender}>
              <span style={{ fontSize: '10px' }}>🔘</span>
              {buttonText}
            </ButtonResponseChip>
          </Tooltip>
        );
      } catch (error) {
        console.error('Error rendering button response:', error);
        return null;
      }
    });
  };

  const renderMessage = () => {
    // If this is a reaction message, don't render it as a separate message
    if (message?.type === "reaction") {
      return null; // Reactions will be handled by their parent messages
    }
    
    // If this is a button response message, don't render it as a separate message
    if (message?.type === "button" && message?.context?.id) {
      return null; // Button responses will be handled by their parent messages
    }
    
    if (!message || !message.from || !message.timestamp) {
      return (
        <MessageContainer isSender={true}>
          <MessageBubble isSender={true}>
            <Typography variant="body2" sx={{ color: 'inherit' }}>
              This message is not supported
            </Typography>
            <TimeStamp isSender={true}>
              {message ? formatTime(message.timestamp) : ""}
            </TimeStamp>
          </MessageBubble>
          <AvatarStyled isSender={true} src="/profile1.jpg">
            {!message?.from && 'U'}
          </AvatarStyled>
        </MessageContainer>
      );
    } else if (message?.from === process.env.REACT_APP_PHONE_NUMBER) {
      const normalizedBody = getNormalizedTextBody(message);
      return (
        <MessageContainer isSender={true} isStatus={message.custom_status}>
          {!message.custom_status && (
            <AvatarStyled isSender={true} src="/icon.png">
              M
            </AvatarStyled>
          )}
          <MessageBubble isSender={true} isStatus={message.custom_status}>
            {normalizedBody?.name ? (
              <TemplateMessage message={message} sendTemplateMessage={props.sendTemplateMessage} cancelTemplateMessage={props.cancelTemplateMessage} />
            ) : message?.text?.body ? (
              typeof normalizedBody === "string" ? (
                <Typography variant="body2" sx={{ color: 'inherit', lineHeight: 1.4 }}>
                  {normalizedBody}
                </Typography>
              ) : (
                normalizedBody?.type === "text" ? (
                  <Typography variant="body2" sx={{ color: 'inherit', lineHeight: 1.4 }}>
                    {normalizedBody?.text?.body || normalizedBody?.body || ""}
                  </Typography>
                ) : (
                  <MediaMessage type={normalizedBody?.type || message?.type} media={normalizedBody || message} isSender={true} />
                )
              )
            ) : message?.type && (message.type === "file" || message.type === "image" || message.type === "video" || message.type === "audio" || message.type === "document") ? (
              <MediaMessage type={message.type} media={message} isSender={true} />
            ) : (
              <Typography variant="body2" sx={{ color: 'inherit' }}>
                This message is not supported
              </Typography>
            )}
            <TimeStamp isSender={true}>
              {formatTime(message.timestamp)}
              {!message.custom_status && <StatusMessage statuses={statuses} />}
            </TimeStamp>
            {/* Display reactions for this message */}
            {props.messages && props.messages
              .filter(msg => {
                try {
                  return msg.entry[0].changes[0].value.messages[0].type === "reaction" &&
                         msg.entry[0].changes[0].value.messages[0].reaction.message_id === message.id;
                } catch (error) {
                  return false;
                }
              })
              .map((reactionMsg, idx) => (
                <ReactionChip
                  key={idx}
                  label={reactionMsg.entry[0].changes[0].value.messages[0].reaction.emoji}
                  size="small"
                  isSender={true}
                />
              ))}
            {/* Display button responses for this message */}
            {renderButtonResponses(true)}
          </MessageBubble>
        </MessageContainer>
      );
    } else if (message?.interactive?.nfm_reply) {
      return (
        <MessageContainer isSender={true}>
          <MessageBubble isSender={true}>
            <SurveyMessage responseJson={message.interactive.nfm_reply.response_json} />
            <TimeStamp isSender={true}>
              {formatTime(message.timestamp)}
            </TimeStamp>
          </MessageBubble>
          <AvatarStyled isSender={true} src="/icon.png">
            M
          </AvatarStyled>
        </MessageContainer>
      );
    } else {
      const normalizedBody = getNormalizedTextBody(message);
      return (
        <MessageContainer isSender={false}>
          <AvatarStyled isSender={false} src="/profile1.jpg">
            C
          </AvatarStyled>
          <MessageBubble isSender={false}>
            {message?.type === "text" ? (
              <Typography variant="body2" sx={{ color: 'inherit', lineHeight: 1.4 }}>
                {typeof normalizedBody === "string"
                  ? normalizedBody
                  : (normalizedBody?.text?.body || normalizedBody?.body || normalizedBody?.name || "[Mensaje]")}
              </Typography>
            ) : (
              <MediaMessage type={message?.type} media={message} isSender={false} />
            )}
            {message?.type === "button" && (
              <PayloadMessage message={message} />
            )}
            <TimeStamp isSender={false}>
              {formatTime(message.timestamp)}
            </TimeStamp>
            {/* Display reactions for this message */}
            {props.messages && props.messages
              .filter(msg => {
                try {
                  return msg.entry[0].changes[0].value.messages[0].type === "reaction" &&
                         msg.entry[0].changes[0].value.messages[0].reaction.message_id === message.id;
                } catch (error) {
                  return false;
                }
              })
              .map((reactionMsg, idx) => (
                <ReactionChip
                  key={idx}
                  label={reactionMsg.entry[0].changes[0].value.messages[0].reaction.emoji}
                  size="small"
                  isSender={false}
                />
              ))}
            {/* Display button responses for this message */}
            {renderButtonResponses(false)}
          </MessageBubble>
        </MessageContainer>
      );
    }
  };

  const handleCatch = () => {
    setError(true);
  };

  return <>{renderMessage()}</>;
}

export default Message;